package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.domain.billing.PlanLimits
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.billing.WorkplacePlan
import com.evoapps.evoshift.domain.repository.PlanRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/** `workplaces/{id}/plan/access` as functions/planAccess.js writes it (SH-52). */
@Serializable
internal data class WorkplacePlanDto(
    val status: String? = null,
    val activeUntil: String? = null,
    val trialEndsAt: String? = null,
    /** Null: unlimited (the trial). */
    val seats: Int? = null,
    val seatsUsed: Int = 0,
    /** SH-220: people past [seats]; growing past the plan never blocks. */
    val overSeats: Int = 0,
    /** SH-220: since when the team is past [seats]; null while it fits. */
    val overSince: String? = null
)

internal fun WorkplacePlanDto.toDomain(): WorkplacePlan? {
    val tier = PlanTier.entries.firstOrNull { it.name == status } ?: return null
    return WorkplacePlan(
        tier = tier,
        activeUntil = activeUntil?.let { runCatching { Instant.parse(it) }.getOrNull() },
        trialEndsAt = trialEndsAt?.let { runCatching { Instant.parse(it) }.getOrNull() },
        seats = seats ?: PlanLimits.UNLIMITED,
        seatsUsed = seatsUsed,
        overSeats = overSeats.coerceAtLeast(0),
        overSince = overSince?.let { runCatching { Instant.parse(it) }.getOrNull() }
    )
}

class PlanRepositoryImpl(private val firestore: AppFirestore) : PlanRepository {
    override fun observeWorkplacePlan(workplaceId: String): Flow<WorkplacePlan?> =
        firestore.collection("workplaces").document(workplaceId).collection("plan").document("access")
            .safeSnapshots
            .map { snapshot ->
                // Unreadable (a field the function adds later) = unknown, which never locks anyone out.
                if (!snapshot.exists) null else runCatching { snapshot.data<WorkplacePlanDto>().toDomain() }.getOrNull()
            }
            .distinctUntilChanged()
}
